import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { formatApiError } from "../../lib/api";
import { getCannibalDevice, listCannibalDevices, listItemMaster } from "../../lib/serviceApi";
import SearchableSelect from "../SearchableSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const rowKey = () => `cannibal-part-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function activeSavedUsages(ticket) {
  return (ticket?.cannibal_usages || []).filter((row) => row.usage_status === "active");
}

function savedSelection(ticket) {
  const usages = activeSavedUsages(ticket);
  return {
    donorId: usages[0]?.cannibal_device_id || "",
    donorSerialNumber: usages[0]?.cannibal_device_serial_number || "",
    itemIds: [...new Set(usages.map((row) => row.item_id).filter(Boolean))].sort(),
    rows: usages.map((usage) => ({
      key: `saved:${usage.id || usage.item_id}`,
      usageId: usage.id || "",
      itemId: usage.item_id || "",
      model: usage.model || "",
      part_number: usage.part_number || "",
      part_name: usage.part_name || "",
      source_type: "cannibal",
      saved: true,
    })),
  };
}

function unverifiedSavedDonor(saved, snapshot) {
  return {
    id: saved.donorId,
    serial_number: snapshot?.cannibal_device_serial_number || "Current donor",
    model: snapshot?.cannibal_device_model || "",
    unverified: true,
  };
}

const SAVED_DONOR_UNVERIFIED_MESSAGE =
  "The saved Cannibal donor could not be verified. Select an active same-Branch donor to continue.";

export function isCannibalPartDonorEligible(donor, ticketBranch) {
  return Boolean(
    donor?.status === "active" &&
    !donor.transferred_at &&
    !donor.transferred_service_ticket_id &&
    donor.branch === ticketBranch
  );
}

export default function CannibalPartSelector({
  ticket,
  active,
  disabled = false,
  onStateChange,
  fieldClassName = "",
  triggerClassName = "h-[42px]",
  inventoryContent = null,
}) {
  const saved = useMemo(() => savedSelection(ticket), [ticket]);
  const savedSignature = `${ticket?.id || ""}:${ticket?.updated_at || ""}:${saved.donorId}:${saved.itemIds.join("|")}`;
  const [donors, setDonors] = useState([]);
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(Boolean(saved.donorId));
  const [donorId, setDonorId] = useState(saved.donorId);
  const [rows, setRows] = useState(saved.rows);
  const [selectedIds, setSelectedIds] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draftModel, setDraftModelState] = useState("");
  const [draftItemId, setDraftItemId] = useState("");
  const [loading, setLoading] = useState(false);
  const [donorSearchLoading, setDonorSearchLoading] = useState(false);
  const [error, setError] = useState("");
  const [hydrationError, setHydrationError] = useState("");
  const [pendingReset, setPendingReset] = useState(null);
  const donorSearchRequest = useRef(0);

  useEffect(() => {
    setEnabled(Boolean(saved.donorId));
    setDonorId(saved.donorId);
    setRows(saved.rows);
    setSelectedIds([]);
    setAdding(false);
    setDraftModelState("");
    setDraftItemId("");
    setHydrationError("");
    setPendingReset(null);
  // The signature deliberately represents authoritative post-save hydration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSignature]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setHydrationError("");
    Promise.all([
      listCannibalDevices({ branch: ticket?.branch, status: "active", page: 1, page_size: 100 }),
      listItemMaster(),
    ]).then(async ([donorResponse, itemResponse]) => {
      if (cancelled) return;
      const available = Array.isArray(donorResponse?.data) ? [...donorResponse.data] : [];
      const snapshot = activeSavedUsages(ticket).find((row) => row.cannibal_device_id === saved.donorId);
      if (saved.donorId && !available.some((row) => row.id === saved.donorId)) {
        let hydratedDonor = null;
        try {
          const donorDetail = await getCannibalDevice(saved.donorId);
          const donor = donorDetail?.data;
          if (donor?.id === saved.donorId) {
            hydratedDonor = {
              ...donor,
              retained_only: !isCannibalPartDonorEligible(donor, ticket?.branch),
            };
          }
        } catch {
          // The saved identity remains readable below; eligibility stays fail-closed.
        }
        if (cancelled) return;
        if (!hydratedDonor) setHydrationError(SAVED_DONOR_UNVERIFIED_MESSAGE);
        available.unshift(hydratedDonor || unverifiedSavedDonor(saved, snapshot));
      }
      setDonors(available);
      setItems(Array.isArray(itemResponse) ? itemResponse : []);
    }).catch((reason) => {
      if (!cancelled) setError(formatApiError(reason));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [active, saved, ticket?.branch, ticket]);

  const searchDonors = useCallback((search) => {
    if (!active) return;
    const requestId = ++donorSearchRequest.current;
    setDonorSearchLoading(true);
    setError("");
    listCannibalDevices({
      branch: ticket?.branch,
      status: "active",
      search: search || undefined,
      page: 1,
      page_size: 100,
    }).then((response) => {
      if (requestId !== donorSearchRequest.current) return;
      const available = Array.isArray(response?.data) ? [...response.data] : [];
      setDonors((current) => {
        const selected = current.find((row) => row.id === donorId);
        if (selected && !available.some((row) => row.id === selected.id)) available.unshift(selected);
        return available;
      });
    }).catch((reason) => {
      if (requestId === donorSearchRequest.current) setError(formatApiError(reason));
    }).finally(() => {
      if (requestId === donorSearchRequest.current) setDonorSearchLoading(false);
    });
  }, [active, donorId, ticket?.branch]);

  useEffect(() => {
    if (!items.length) return;
    setRows((current) => current.map((row) => {
      const item = items.find((candidate) => candidate.id === row.itemId);
      if (!item) return row;
      return {
        ...row,
        model: item.model || "",
        part_number: item.part_number || row.part_number,
        part_name: item.part_name || row.part_name,
      };
    }));
  }, [items]);

  const selectedDonor = donors.find((row) => row.id === donorId);
  const selectedDonorTransferred = selectedDonor?.status === "transferred";
  const itemIds = useMemo(() => rows.map((row) => row.itemId).filter(Boolean).sort(), [rows]);
  const changed = (enabled ? donorId : "") !== saved.donorId || itemIds.join("|") !== saved.itemIds.join("|");
  const blockedItemIds = new Set(
    (selectedDonor?.removed_parts || [])
      .filter((row) => row.origin === "pre_existing" || (row.origin === "cannibal_usage" && row.usage_status === "active"))
      .map((row) => row.item_id)
      .filter((itemId) => !(saved.donorId === donorId && saved.itemIds.includes(itemId)))
  );
  const models = [...new Set(items.flatMap((item) => [item.model, ...(item.compatible_models || [])]).filter(Boolean))].sort();
  const modelOptions = models.map((model) => ({ value: model, label: model }));
  const itemOptions = items
    .filter((item) => !draftModel || item.model === draftModel || (item.compatible_models || []).includes(draftModel))
    .filter((item) => !blockedItemIds.has(item.id) && !itemIds.includes(item.id))
    .map((item) => ({ value: item.id, label: item.part_name, sublabel: item.part_number }));
  const donorOptions = donors.map((donor) => ({
    value: donor.id,
    label: `${donor.serial_number} · ${donor.model || "Unknown model"}`,
    sublabel: donor.unverified
      ? "Saved donor unavailable"
      : donor.retained_only && donor.status === "transferred"
        ? "Existing donor has been transferred"
        : donor.retained_only
          ? "Existing selection only"
          : donor.branch,
    sublabelClassName: donor.retained_only && donor.status === "transferred" ? "text-warning" : undefined,
  }));

  const applyReset = (action) => {
    if (action.type === "disable") {
      setEnabled(false);
      setDonorId("");
    } else {
      setDonorId(action.donorId);
      setHydrationError("");
    }
    setRows([]);
    setSelectedIds([]);
    setAdding(false);
    setDraftModelState("");
    setDraftItemId("");
    setPendingReset(null);
  };
  const requestReset = (action) => {
    const changesSavedDonorWithUsage = action.type === "donor" &&
      donorId === saved.donorId && saved.rows.length > 0;
    if (changesSavedDonorWithUsage || rows.length) {
      setPendingReset({ ...action, changesSavedDonorWithUsage });
      return;
    }
    applyReset(action);
  };
  const setDraftModel = (value) => {
    setDraftModelState(value);
    setDraftItemId("");
  };
  const saveDraft = () => {
    const item = items.find((candidate) => candidate.id === draftItemId);
    if (!item || !draftModel || itemIds.includes(item.id) || blockedItemIds.has(item.id)) return;
    setRows((current) => [...current, {
      key: rowKey(),
      usageId: "",
      itemId: item.id,
      model: item.model || draftModel,
      part_number: item.part_number || "",
      part_name: item.part_name || "",
      source_type: "cannibal",
      saved: false,
    }]);
    setAdding(false);
    setDraftModelState("");
    setDraftItemId("");
  };
  const removeSelected = () => {
    setRows((current) => current.filter((row) => !selectedIds.includes(row.key)));
    setSelectedIds([]);
  };
  const toggleSelection = (key) => setSelectedIds((current) => current.includes(key)
    ? current.filter((value) => value !== key)
    : [...current, key]);
  const selectedDonorLabel = selectedDonor?.serial_number || saved.donorSerialNumber || "";
  const canAdd = Boolean(
    active && enabled && donorId && isCannibalPartDonorEligible(selectedDonor, ticket?.branch) &&
    !disabled && !loading && !error && !hydrationError
  );

  const ledger = {
    rows,
    selectedIds,
    toggleSelection,
    removeSelected,
    canRemove: active && !disabled,
    canAdd,
    adding,
    startAdd: () => setAdding(true),
    draftModel,
    setDraftModel,
    draftItemId,
    setDraftItemId,
    modelOptions,
    itemOptions,
    saveDraft,
    disabled,
    transferred: Boolean(enabled && donorId && selectedDonorTransferred),
  };
  const state = useMemo(() => ({
    enabled,
    donorId: enabled ? donorId : "",
    donorLabel: enabled ? selectedDonorLabel : "",
    savedDonorLabel: saved.donorSerialNumber,
    savedDonorId: saved.donorId,
    itemIds: enabled ? itemIds : [],
    rows,
    savedRows: saved.rows,
    loading,
    error: error || hydrationError,
    loaded: active && !loading && !error && !hydrationError,
    changed,
    hasSelection: rows.length > 0,
  }), [
    active,
    changed,
    donorId,
    enabled,
    error,
    hydrationError,
    itemIds,
    loading,
    rows,
    saved.donorId,
    saved.donorSerialNumber,
    saved.rows,
    selectedDonorLabel,
  ]);

  useEffect(() => { onStateChange?.(state); }, [onStateChange, state]);

  return (
    <>
      <div className={fieldClassName} data-testid="cannibal-device-field">
        <label className={`flex h-4 w-fit items-center gap-2 text-xs font-semibold text-zinc-700 dark:text-zinc-300 ${!active ? "text-zinc-400 dark:text-zinc-500" : ""}`}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => event.target.checked ? setEnabled(true) : requestReset({ type: "disable" })}
            disabled={disabled || !active}
            className="h-4 w-4 shrink-0 rounded-sm border-zinc-300 accent-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700"
            aria-label="Enable Cannibal Device"
          />
          <span>Cannibal Device</span>
        </label>
        <div className="mt-2 min-w-0">
          <SearchableSelect
            value={donorId}
            onChange={(value) => value !== donorId && requestReset({ type: "donor", donorId: value })}
            options={donorOptions}
            placeholder={enabled ? "Select active same-Branch donor" : "Available for Part Replace"}
            disabled={disabled || !active || !enabled || loading}
            testid="cannibal-donor-select"
            allowClear
            portal
            containerClassName="min-w-0"
            triggerClassName={`${triggerClassName} rounded-sm px-3 py-2.5 text-sm`}
            showSelectedSublabel={false}
            onSearchChange={searchDonors}
            searchLoading={donorSearchLoading}
            renderSelected={(option) => (
              <span className="inline-flex max-w-full items-center gap-1.5">
                {selectedDonorTransferred ? (
                  <span
                    className="inline-flex shrink-0 text-warning"
                    title="Transferred"
                    aria-label="Transferred"
                    data-testid="cannibal-donor-transferred-icon"
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                ) : null}
                <span className="truncate">{option.label}</span>
              </span>
            )}
          />
        </div>
        {error || hydrationError ? <p className="mt-1 text-xs text-destructive">{error || hydrationError}</p> : null}
      </div>

      {typeof inventoryContent === "function" ? inventoryContent(ledger) : inventoryContent}

      <AlertDialog open={Boolean(pendingReset)} onOpenChange={(open) => !open && setPendingReset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingReset?.changesSavedDonorWithUsage ? "Change Cannibal Device?" : "Clear selected Cannibal parts?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReset?.changesSavedDonorWithUsage
                ? `Existing Cannibal parts from ${selectedDonorLabel} will be reversed when you save. Inventory parts will not be affected.`
                : "Changing or disabling the donor removes every selected Cannibal part from this local reconciliation draft."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyReset(pendingReset)}>
              {pendingReset?.changesSavedDonorWithUsage ? "Confirm" : "Clear Parts"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
